'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { ShoppingCart } from 'lucide-react';
import { routeStorefrontMediaInHtml } from '@/lib/media-url';
import type {
  CameraBracketNavGroup,
  CameraBracketProduct,
  CameraBracketTab,
} from './camera-brackets.types';

interface CameraBracketsPageClientProps {
  tabs: CameraBracketTab[];
  navigationGroups?: CameraBracketNavGroup[];
}

function formatPrice(value: string | null): string | null {
  if (!value) {
    return null;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(numericValue);
}

function getCartSessionId(): string {
  const key = 'cart_session_id';
  const existing = window.localStorage.getItem(key);

  if (existing) {
    return existing;
  }

  const randomId =
    typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const sessionId = `session-${randomId}`;
  window.localStorage.setItem(key, sessionId);
  return sessionId;
}

function createDefaultNavigationGroups(tabs: CameraBracketTab[]): CameraBracketNavGroup[] {
  const grouped = new Map<string, CameraBracketNavGroup>();

  for (const tab of tabs) {
    if (!tab.group) {
      continue;
    }

    const group = grouped.get(tab.group) ?? { label: tab.group, items: [] };
    group.items.push({ label: tab.label, tabId: tab.id });
    grouped.set(tab.group, group);
  }

  return Array.from(grouped.values());
}

function ProductPrice({ product }: { product: CameraBracketProduct }) {
  const basePrice = formatPrice(product.basePrice);
  const specialPrice = formatPrice(product.specialPrice);

  if (!basePrice && !specialPrice) {
    return null;
  }

  return (
    <div className="camera-brackets-product-price">
      {specialPrice ? (
        <>
          <span>{specialPrice}</span>
          {basePrice ? <span className="camera-brackets-old-price">{basePrice}</span> : null}
        </>
      ) : (
        <span>{basePrice}</span>
      )}
    </div>
  );
}

const MAGENTO_PRODUCT_URL_OVERRIDES: Readonly<Record<string, string>> = {
  'Ceiling Pendant Base Mount for Lookout, Laser, Spotlight, Scope, Beacon - PAB26ZV':
    'https://www.getscw.com/ceiling-pendant-mount-for-the-lookout-the-laser-the-spotlight-and-the-scope-pab26zv.php',
  'Electrical Box Mount for The Deputy v2, The Deputy v3, The Deputy v4, Deputy 8.0 v5, The Sheriff, The Scout and The Informant - EMB26DFD':
    'https://www.getscw.com/electrical-box-mount-for-the-sheriff-or-the-informant-or-the-deputy-v2-emb26dfd',
  'Electrical Box Mount for The Detective, Radius 5.0, Garrison 5.0 - EBM26DFT':
    'https://www.getscw.com/electrical-box-mount-for-the-deputy-v1-or-the-detective-ebmd26df',
  'Electrical Box Mount for The Radius 12.0, Bastion 4.0 PTZ, and The Judge - EBM26P':
    'https://www.getscw.com/electrical-box-mount-for-the-radius-ebm26p',
  'Electrical Box Transfer Plate for Radius 12.0 and Judge - EBM26TP':
    'https://www.getscw.com/electrical-box-transfer-plate-for-radius-and-judge-ebm26tp',
  'Outdoor Wall Mount Bracket for The Detective and Radius 5.0 - OWM26DF':
    'https://www.getscw.com/outdoor-wall-mount-bracket-for-the-deputy-v1-or-the-detective-owm26df',
  'Pole Mount Bracket for Lookout, Laser, Watchkeeper, Spotlight, Watchkeeper, Scope, Beacon - PMB26ZV':
    'https://www.getscw.com/pole-mount-bracket-for-the-lookout-the-laser-the-spotlight-and-the-scope-pmb26zv.php',
  'Round Electrical Box Mount for The Paladin 8.0, The Warrior 8.0v1, The Warrior 4.0 v3 and v5, The Warrior 8.0v3 - EMB26BFR':
    'https://www.getscw.com/electrical-box-mount-for-the-warrior-8-v1-emb26bf8',
  'Short Wall Mount for Lookout, Laser, Watchkeeper, Spotlight, Scope - SWM26ZV':
    'https://www.getscw.com/short-wall-mount-for-the-lookout-the-laser-the-spotlight-and-the-scope-swm26zv.php',
  'Square Electrical Box Mount for The Warrior 2.0v1, The Warrior 4.0v1, all Warrior v2s, The Warrior 2.0v3 - EMB26BF':
    'https://www.getscw.com/electrical-box-mount-for-the-warrior-2-4-emb26bf',
  'Swan Neck Mount for Lookout, Laser, Spotlight, Scope, Beacon - SN26ZV':
    'https://www.getscw.com/swan-neck-parapet-mount-for-the-lookout-the-laser-the-spotlight-and-the-scope-sn26zv.php',
};

function getMagentoProductUrl(product: CameraBracketProduct): string {
  const normalizedName = product.name.replace(/\s+/g, ' ').trim();
  return MAGENTO_PRODUCT_URL_OVERRIDES[normalizedName]
    ?? `https://www.getscw.com/${product.slug}`;
}

function getMagentoCartAction(magentoId: number | null): string | undefined {
  if (magentoId === null) {
    return undefined;
  }

  return `https://www.getscw.com/checkout/cart/add/uenc/aHR0cHM6Ly93d3cuZ2V0c2N3LmNvbS9jY3R2LWFjY2Vzc29yaWVzL2NhbWVyYS1icmFja2V0cw~~/product/${magentoId}/`;
}

function ProductRow({ product }: { product: CameraBracketProduct }) {
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const productUrl = getMagentoProductUrl(product);
  const cartAction = getMagentoCartAction(product.magentoId);

  async function handleAddToCart() {
    if (!product.canAddToCart || isAdding) {
      return;
    }

    setIsAdding(true);
    setError(null);

    try {
      const response = await fetch('/api/cart/items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: getCartSessionId(),
          productId: product.id,
          quantity: 1,
          selectedOptions: [],
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to add this bracket to cart.');
      }

      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch {
      setError('Unable to add this bracket to cart.');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <article className="camera-brackets-product" aria-label={product.name}>
      <a className="camera-brackets-product-image" href={productUrl}>
        {product.mainImage ? (
          <img
            src={product.mainImage.url}
            alt={product.name}
            width="400"
            height="400"
          />
        ) : (
          <span>No image</span>
        )}
      </a>
      <div className="camera-brackets-product-body">
        <p>
          <a href={productUrl}>{product.name}</a>
        </p>
        <p className="camera-brackets-stock">{product.stockLabel}</p>
        <ProductPrice product={product} />
        {product.canAddToCart ? (
          <form
            action={cartAction}
            method="post"
            onSubmit={(event) => {
              event.preventDefault();
              void handleAddToCart();
            }}
          >
            <button
              type="submit"
              className="camera-brackets-cart-button"
              disabled={isAdding}
            >
              {isAdding ? (
                'Adding...'
              ) : (
                <>
                  <ShoppingCart aria-hidden="true" size={14} />
                  Add to Cart
                </>
              )}
            </button>
          </form>
        ) : (
          <p className="camera-brackets-unavailable">Currently unavailable</p>
        )}
        {error ? <p className="camera-brackets-error">{error}</p> : null}
        {product.shortDescription ? (
          <div
            className="camera-brackets-description"
            dangerouslySetInnerHTML={{ __html: routeStorefrontMediaInHtml(product.shortDescription) }}
          />
        ) : null}
      </div>
    </article>
  );
}

export function CameraBracketsPageClient({
  tabs,
  navigationGroups,
}: CameraBracketsPageClientProps) {
  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id ?? '');
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const navigationRef = useRef<HTMLElement | null>(null);
  const tabMap = useMemo(() => new Map(tabs.map((tab) => [tab.id, tab])), [tabs]);
  const primaryTab = tabs[0];
  const activeTab = tabMap.get(activeTabId) ?? primaryTab;
  const groups = navigationGroups ?? createDefaultNavigationGroups(tabs);

  useEffect(() => {
    const navigation = navigationRef.current;
    if (!navigation) {
      return;
    }

    const dropdownLinks = navigation.querySelectorAll<HTMLAnchorElement>(
      'a[data-toggle="dropdown"]',
    );
    dropdownLinks.forEach((link) => {
      link.setAttribute('href', 'javascript:void(0)');
    });
  }, []);

  useEffect(() => {
    if (!openGroup) {
      return;
    }

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (!navigationRef.current?.contains(target)) {
        setOpenGroup(null);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [openGroup]);

  if (!activeTab) {
    return (
      <div className="camera-brackets-page">
        <section className="camera-brackets-shell">
          <header className="camera-brackets-header">
            <h1>Security Camera Brackets</h1>
            <p>No bracket categories are currently available.</p>
          </header>
        </section>
      </div>
    );
  }

  function selectTab(tabId: string) {
    if (!tabMap.has(tabId)) {
      return;
    }

    setActiveTabId(tabId);
    setOpenGroup(null);
  }

  return (
    <div className="camera-brackets-page">
        <section className="camera-brackets-shell">
          <header className="camera-brackets-header">
            <nav className="camera-brackets-breadcrumbs" aria-label="Breadcrumb">
              <a href="https://www.getscw.com/">Home</a>
              <span aria-hidden="true">&gt;</span>
              <span>SCW Security Camera Brackets</span>
            </nav>
            <h1>Security Camera Brackets</h1>
            <p>Mounting brackets enable you to find the best possible placement for your cameras.</p>
            <p>Reminder: Brackets are not universal!</p>
          </header>

        <nav
          ref={navigationRef}
          className="camera-brackets-tabs"
          aria-label="Camera bracket categories"
        >
          {primaryTab ? (
            <a
              href={`#${primaryTab.id}`}
              role="tab"
              data-toggle="tab"
              aria-selected={activeTab.id === primaryTab.id}
              className={activeTab.id === primaryTab.id ? 'is-active' : ''}
              onClick={(event) => {
                event.preventDefault();
                selectTab(primaryTab.id);
              }}
            >
              {primaryTab.label}
            </a>
          ) : null}
          {groups.map((group) => (
            <div className="camera-brackets-tab-group" key={group.label}>
              <a
                href="#"
                role="button"
                data-toggle="dropdown"
                className={openGroup === group.label ? 'is-open' : ''}
                aria-expanded={openGroup === group.label}
                onClick={(event) => {
                  event.preventDefault();
                  setOpenGroup(openGroup === group.label ? null : group.label);
                }}
              >
                {group.label}
              </a>
              <div
                className="camera-brackets-tab-menu"
                hidden={openGroup !== group.label}
              >
                {group.items.map((item) => (
                  <a
                    href={`#${item.tabId}`}
                    role="tab"
                    data-toggle="tab"
                    aria-selected={activeTab.id === item.tabId}
                    className={activeTab.id === item.tabId ? 'is-active' : ''}
                    key={`${group.label}-${item.label}-${item.tabId}`}
                    onClick={(event) => {
                      event.preventDefault();
                      selectTab(item.tabId);
                    }}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {tabs.map((tab) => (
          <section
            className="camera-brackets-panel"
            role="tabpanel"
            hidden={activeTab.id !== tab.id}
            key={tab.id}
          >
            <div className="camera-brackets-panel-heading">
              <h2>{tab.heading}</h2>
              {tab.lead?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </div>
            <div className="camera-brackets-products">
              {tab.products.length > 0 ? (
                tab.products.map((product) => (
                  <ProductRow product={product} key={`${tab.id}-${product.id}`} />
                ))
              ) : (
                <p className="camera-brackets-empty">No compatible brackets are currently listed.</p>
              )}
            </div>
          </section>
        ))}
      </section>
    </div>
  );
}
